import * as React from "react"
import { cva, type VariantProps } from "cva"
import { Slot } from "@radix-ui/react-slot"
import { clx } from "../utils/clx"

const badgeColorVariants = cva({
  variants: {
    color: {
      grey: "bg-grey-20 text-ui-fg-subtle [&_svg]:text-ui-fg-muted border-ui-border-strong"
    },
  },
  defaultVariants: {
    color: "grey",
  },
})

const badgeSizeVariants = cva({
  base: "inline-flex items-center gap-x-0.5 border",
  variants: {
    size: {
      base: "txt-compact-small-plus px-2 py-0.5"
    },
    rounded: {
      base: "rounded-md",
      full: "rounded-full",
    },
  },
  defaultVariants: {
    size: "base",
    rounded: "base",
  },
})

interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color">,
    VariantProps<typeof badgeSizeVariants>,
    VariantProps<typeof badgeColorVariants> {
  asChild?: boolean
}

/**
 * This component is based on the `div` element and supports all of its props
 */
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      /**
       * The badge's size.
       */
      size = "base",
      /**
       * The style of the badge's border radius.
       */
      rounded = "base",
      /**
       * The badge's color.
       */
      color = "grey",
      /**
       * Whether to remove the wrapper `span` element and use the
       * passed child element instead.
       */
      asChild = false,
      ...props
    }: BadgeProps,
    ref
  ) => {
    const Component = asChild ? Slot : "span"

    return (
      <Component
        ref={ref}
        className={clx(
          badgeColorVariants({ color }),
          badgeSizeVariants({ size, rounded }),
          className
        )}
        {...props}
      />
    )
  }
)
Badge.displayName = "Badge"

export { Badge, badgeColorVariants }
